import { useMemo } from 'react';
import { useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

/**
 * Хук для определения типа устройства на основе брейкпоинтов Mantine.
 * Возвращает флаги для мобильных, планшетных и десктопных разрешений.
 *
 * @returns Объект с булевыми значениями `isMobile`, `isTablet`, `isDesktop`.
 *
 * @example
 * ```tsx
 * const { isMobile, isTablet, isDesktop } = useDevice();
 *
 * if (isMobile) {
 *   return <MobileLayout />;
 * }
 *
 * if (isTablet) {
 *   return <TabletLayout />;
 * }
 *
 * return <DesktopLayout />;
 * ```
 */
export const useDevice = (): { isMobile: boolean; isTablet: boolean; isDesktop: boolean } => {
  const theme = useMantineTheme();

  const greaterOrEqualSm = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, false, {
    getInitialValueInEffect: false,
  });
  const greaterOrEqualLg = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`, false, {
    getInitialValueInEffect: false,
  });

  return useMemo(() => {
    const isDesktop = greaterOrEqualLg;
    const isMobile = !greaterOrEqualSm;
    const isTablet = !isMobile && !isDesktop;

    return { isMobile, isTablet, isDesktop };
  }, [greaterOrEqualLg, greaterOrEqualSm]);
};

export default useDevice;
